import { logger } from '../utils/logger';
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft,
  Brain,
  ClipboardList,
  FileImage,
  Clock,
  Activity,
  Layers,
  Pill,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import PatientInfoCard from '../components/case-detail/PatientInfoCard';
import OverviewTab from '../components/case-detail/OverviewTab';
import ExamTab from '../components/case-detail/ExamTab';
import AnalysisTab from '../components/case-detail/AnalysisTab';

import ImageUploader from '../components/ImageUploader';
import ExamTimeline from '../components/ExamTimeline';
import LesionProgression from '../components/LesionProgression';
import DicomSeriesViewer from '../components/DicomSeriesViewer';
import TreatmentLog from '../components/TreatmentLog';

import { BACKEND_URL } from '../config/api';

const CaseDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [exams, setExams] = useState([]);
  const [analyses, setAnalyses] = useState([]);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [analyzing, setAnalyzing] = useState(false);
  const [latestAnalysis, setLatestAnalysis] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [breedSearch, setBreedSearch] = useState('');
  const [breedDropdownOpen, setBreedDropdownOpen] = useState(false);
  const breedRef = useRef(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [examMode, setExamMode] = useState('list');
  const [chatMessage, setChatMessage] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const chatEndRef = useRef(null);

  // Close breed dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (breedRef.current && !breedRef.current.contains(e.target)) {
        setBreedDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/cases/patients/${id}`,
        editForm,
        { withCredentials: true }
      );
      setPatient(data);
      setEditing(false);
      toast.success('Patient info updated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  // Fetch patient, exams, analyses
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [patientRes, examsRes, analysesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/cases/patients/${id}`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/neuro/patients/${id}/exams`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/neuro/analyses/${id}`, { withCredentials: true })
        ]);
        setPatient(patientRes.data);
        const examItems = examsRes.data.items || examsRes.data;
        const analysisItems = analysesRes.data.items || analysesRes.data;
        setExams(examItems);
        setAnalyses(analysisItems);
        if (analysisItems.length > 0) {
          setLatestAnalysis(analysisItems[analysisItems.length - 1]);
        }
      } catch (error) {
        logger.error('Error fetching data:', error);
        if (error.response?.status === 404) {
          toast.error('Patient not found');
          navigate('/cases');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Auto-scroll to latest chat message
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [latestAnalysis?.chat_messages?.length]);

  const handleExamSubmit = async (examData) => {
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/neuro/exams`,
        { ...examData, patient_id: id },
        { withCredentials: true }
      );
      setExams([...exams, data]);
      toast.success('Neurological exam saved');

      setAnalyzing(true);
      setActiveTab('analysis');
      try {
        const analysisRes = await axios.post(
          `${BACKEND_URL}/api/neuro/analyze`,
          { exam_id: data.id, patient_id: id },
          { withCredentials: true }
        );
        setLatestAnalysis(analysisRes.data);
        setAnalyses([...analyses, analysisRes.data]);
        toast.success('AI analysis complete');
      } catch (analysisError) {
        toast.error('Analysis failed. Please try again.');
      } finally {
        setAnalyzing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save exam');
    }
  };

  const handleOpenExam = async (exam) => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/neuro/exams/${exam.id}`, { withCredentials: true });
      setSelectedExam(data);
      setExamMode(data.is_locked ? 'view' : 'edit');
      setActiveTab('exam');
    } catch (error) {
      toast.error('Failed to load exam');
    }
  };

  const handleEditExam = async (examData) => {
    if (!selectedExam) return;
    try {
      const { data } = await axios.put(
        `${BACKEND_URL}/api/neuro/exams/${selectedExam.id}`,
        { ...examData, patient_id: id },
        { withCredentials: true }
      );
      setExams(exams.map(e => e.id === data.id ? data : e));
      setSelectedExam(null);
      setExamMode('list');
      toast.success('Exam updated');

      setAnalyzing(true);
      setActiveTab('analysis');
      try {
        const analysisRes = await axios.post(
          `${BACKEND_URL}/api/neuro/analyze`,
          { exam_id: data.id, patient_id: id },
          { withCredentials: true }
        );
        setLatestAnalysis(analysisRes.data);
        setAnalyses([...analyses, analysisRes.data]);
        toast.success('AI analysis updated');
      } catch (analysisError) {
        toast.error('Analysis failed. Please try again.');
      } finally {
        setAnalyzing(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update exam');
    }
  };

  const handleNewExam = () => {
    setSelectedExam(null);
    setExamMode('new');
    setActiveTab('exam');
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/neuro/export-pdf/${id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `VetNeuroHelp_Report_${patient?.name || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF report downloaded');
    } catch (error) {
      toast.error('Failed to export PDF');
      logger.error('PDF export error:', error);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleChatSend = async () => {
    if (!chatMessage.trim() || !latestAnalysis?.id || chatSending) return;
    const msg = chatMessage.trim();
    setChatMessage('');
    setChatSending(true);

    const updatedAnalysis = { ...latestAnalysis };
    const currentMessages = updatedAnalysis.chat_messages || [];
    updatedAnalysis.chat_messages = [
      ...currentMessages,
      { role: 'user', content: msg, created_at: new Date().toISOString() }
    ];
    setLatestAnalysis(updatedAnalysis);

    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/neuro/analyze/chat`,
        { analysis_id: latestAnalysis.id, message: msg },
        { withCredentials: true }
      );
      setLatestAnalysis(prev => ({ ...prev, chat_messages: data.chat_messages }));
      setAnalyses(prev => prev.map(a => a.id === latestAnalysis.id ? { ...a, chat_messages: data.chat_messages } : a));
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      setLatestAnalysis(prev => ({ ...prev, chat_messages: currentMessages }));
    } finally {
      setChatSending(false);
    }
  };

  const handleExportChat = async () => {
    if (!latestAnalysis?.id) return;
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/neuro/export-chat-pdf/${latestAnalysis.id}`,
        { withCredentials: true, responseType: 'blob' }
      );
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `VetNeuroHelp_AIChat_${patient?.name || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Chat exported as PDF');
    } catch (error) {
      toast.error('Failed to export chat');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#4B6651]" />
      </div>
    );
  }

  if (!patient) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="case-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <Button
          variant="ghost"
          onClick={() => navigate('/cases')}
          className="text-[#5C5F5C] hover:text-[#2A2C2A] w-fit"
          data-testid="back-to-cases"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Cases
        </Button>
      </div>

      {/* Patient Info Card */}
      <PatientInfoCard
        patient={patient}
        setPatient={setPatient}
        patientId={id}
        editing={editing}
        setEditing={setEditing}
        editForm={editForm}
        setEditForm={setEditForm}
        saving={saving}
        handleSaveEdit={handleSaveEdit}
        breedSearch={breedSearch}
        setBreedSearch={setBreedSearch}
        breedDropdownOpen={breedDropdownOpen}
        setBreedDropdownOpen={setBreedDropdownOpen}
        breedRef={breedRef}
        exportingPdf={exportingPdf}
        handleExportPdf={handleExportPdf}
      />

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="bg-white border border-[#E5E4E0] p-1 inline-flex w-max sm:w-auto sm:flex-wrap h-auto gap-1">
            <TabsTrigger value="overview" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-overview">
              Overview
            </TabsTrigger>
            <TabsTrigger value="exam" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-exam">
              <ClipboardList className="w-4 h-4 mr-2" />
              Neuro Exam
            </TabsTrigger>
            <TabsTrigger value="images" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-images">
              <FileImage className="w-4 h-4 mr-2" />
              MRI/CT Images
            </TabsTrigger>
            <TabsTrigger value="analysis" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-analysis">
              <Brain className="w-4 h-4 mr-2" />
              AI Analysis
            </TabsTrigger>
            <TabsTrigger value="timeline" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-timeline">
              <Clock className="w-4 h-4 mr-2" />
              Timeline
            </TabsTrigger>
            <TabsTrigger value="progression" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-progression">
              <Activity className="w-4 h-4 mr-2" />
              Progression
            </TabsTrigger>
            <TabsTrigger value="series" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-series">
              <Layers className="w-4 h-4 mr-2" />
              DICOM Series
            </TabsTrigger>
            <TabsTrigger value="treatments" className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white" data-testid="tab-treatments">
              <Pill className="w-4 h-4 mr-2" />
              Treatments
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="overview" className="mt-6">
          <OverviewTab
            exams={exams}
            latestAnalysis={latestAnalysis}
            handleOpenExam={handleOpenExam}
            handleNewExam={handleNewExam}
            setActiveTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="exam" className="mt-6">
          <ExamTab
            exams={exams}
            selectedExam={selectedExam}
            setSelectedExam={setSelectedExam}
            examMode={examMode}
            setExamMode={setExamMode}
            patient={patient}
            handleExamSubmit={handleExamSubmit}
            handleEditExam={handleEditExam}
            handleNewExam={handleNewExam}
            handleOpenExam={handleOpenExam}
          />
        </TabsContent>

        <TabsContent value="images" className="mt-6">
          <ImageUploader
            patientId={id}
            examId={exams.length > 0 ? exams[exams.length - 1].id : null}
          />
        </TabsContent>

        <TabsContent value="analysis" className="mt-6">
          <AnalysisTab
            analyzing={analyzing}
            latestAnalysis={latestAnalysis}
            chatMessage={chatMessage}
            setChatMessage={setChatMessage}
            chatSending={chatSending}
            chatEndRef={chatEndRef}
            handleChatSend={handleChatSend}
            handleExportChat={handleExportChat}
            setActiveTab={setActiveTab}
          />
        </TabsContent>

        <TabsContent value="timeline" className="mt-6">
          <ExamTimeline patientId={id} />
        </TabsContent>

        <TabsContent value="progression" className="mt-6">
          <LesionProgression patientId={id} />
        </TabsContent>

        <TabsContent value="series" className="mt-6">
          <DicomSeriesViewer
            patientId={id}
            examId={exams.length > 0 ? exams[exams.length - 1].id : null}
          />
        </TabsContent>

        <TabsContent value="treatments" className="mt-6">
          <TreatmentLog
            patientId={id}
            examId={exams.length > 0 ? exams[exams.length - 1].id : null}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CaseDetail;
